import { ArrowRight, Zap } from 'lucide-react';

/**
 * Ecosystem Section
 *
 * Shows:
 * 1. Progressive journey: Install → Annotate → Predict → Optimize → Save
 * 2. GPU cloud providers (working implementations from codebase)
 * 3. Inference framework integrations
 */

// Journey step component
const JourneyStep = ({ number, title, description, isLast }: {
    number: number;
    title: string;
    description: string;
    isLast?: boolean;
}) => (
    <div className="flex items-start gap-4 relative">
        <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-volt-500 flex items-center justify-center text-obsidian-900 font-bold text-sm">
                {number}
            </div>
            {!isLast && <div className="w-px h-full bg-volt-500/30 absolute top-10 left-5" />}
        </div>
        <div className="pb-8">
            <h4 className="text-white font-semibold mb-1">{title}</h4>
            <p className="text-sm text-gray-500">{description}</p>
        </div>
    </div>
);

// Provider/framework logo (text-based, clean)
const LogoBadge = ({ name }: { name: string }) => (
    <div className="px-4 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-volt-500/30 transition-colors">
        <span className="text-sm font-medium text-gray-400 hover:text-white transition-colors">{name}</span>
    </div>
);

export const EcosystemSection = () => {
    const journeySteps = [
        { title: 'Install', description: 'One helm command. 5 minutes.' },
        { title: 'Annotate', description: 'Tag workloads you want optimized.' },
        { title: 'Predict', description: 'ML models learn your traffic patterns.' },
        { title: 'Optimize', description: 'Intelligent routing, scaling, hibernation.' },
        { title: 'Save', description: 'Watch GPU costs drop.' },
    ];

    // GPU providers from codebase - actual working implementations
    const gpuProviders = [
        'AWS', 'GCP', 'Azure', 'Nebius', 'RunPod', 'Lambda Labs',
        'CoreWeave', 'Modal', 'Vast.ai', 'Together AI', 'Baseten',
        'Crusoe', 'Anyscale', 'Replicate', 'Fireworks AI'
    ];

    // AI Accelerators - all supported hardware types
    const accelerators = [
        'NVIDIA H100/A100', 'AMD MI300X', 'Intel Gaudi', 'AWS Inferentia',
        'Google TPU', 'Groq LPU', 'Cerebras WSE', 'd-Matrix Corsair'
    ];

    // Inference frameworks from codebase - actual integrations
    const inferenceFrameworks = [
        'vLLM', 'TensorRT-LLM', 'SGLang', 'Ray Serve', 'NVIDIA NIM', 'Triton'
    ];

    return (
        <section className="py-24 lg:py-32 bg-obsidian-900">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">
                        One Intelligence Layer. Every Cloud. Every Chip.
                    </h2>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                        No hyperscaler will optimize for their competitor. Forecaster optimizes across all 12 providers with Multi-Region Intelligence.
                    </p>
                </div>

                {/* Four column layout */}
                <div className="grid lg:grid-cols-4 gap-8">

                    {/* Journey steps */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-6">
                            How it works
                        </div>
                        <div className="relative">
                            {journeySteps.map((step, i) => (
                                <JourneyStep
                                    key={i}
                                    number={i + 1}
                                    title={step.title}
                                    description={step.description}
                                    isLast={i === journeySteps.length - 1}
                                />
                            ))}
                        </div>
                    </div>

                    {/* AI Accelerators */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-6">
                            AI Accelerators
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {accelerators.map((acc) => (
                                <LogoBadge key={acc} name={acc} />
                            ))}
                        </div>
                        <p className="text-xs text-gray-600 mt-4">
                            8+ accelerator types. GPUs, TPUs, ASICs.
                        </p>
                    </div>

                    {/* GPU Providers */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-6">
                            Hyperscalers, Neo Clouds &amp; Serverless — MRI Enabled
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {gpuProviders.map((provider) => (
                                <LogoBadge key={provider} name={provider} />
                            ))}
                        </div>
                        <p className="text-xs text-gray-600 mt-4">
                            12 providers with Multi-Region Intelligence. 30+ regions.
                        </p>
                    </div>

                    {/* Inference Frameworks */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-volt-400 font-medium mb-6">
                            Inference Frameworks
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {inferenceFrameworks.map((framework) => (
                                <LogoBadge key={framework} name={framework} />
                            ))}
                        </div>
                        <p className="text-xs text-gray-600 mt-4">
                            7+ frameworks. Native metrics. Zero code changes.
                        </p>
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-16 text-center">
                    <a
                        href="/trial"
                        className="inline-flex items-center gap-2 text-volt-400 hover:text-volt-300 font-medium transition-colors group"
                    >
                        See full integrations
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>

                {/* Built for what's next */}
                <div className="max-w-4xl mx-auto mt-16">
                    <div className="relative p-8 rounded-2xl bg-gradient-to-br from-volt-500/5 via-transparent to-transparent border border-volt-500/20">
                        <div className="flex items-start gap-6">
                            <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-volt-500 flex items-center justify-center">
                                <Zap className="w-8 h-8 text-obsidian-900" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-3">
                                    Built for what's next.
                                </h3>
                                <p className="text-gray-400 leading-relaxed">
                                    The AI hardware landscape is evolving fast. New accelerators, new providers, new inference patterns.
                                    Forecaster integrates them as they reach production so you always take advantage of the latest
                                    performance and cost improvements — including energy-optimized routing that tracks
                                    Joules per token and routes to the most efficient accelerator.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section divider */}
                <div className="max-w-xl mx-auto mt-24">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
            </div>
        </section>
    );
};
